import {
  Action,
  Badge,
  Card,
  ConfirmModal,
  OrderLineItem,
  SimpleTooltip,
} from 'corso-admin-ui';
import { ShippingClaimLineItemTag } from 'corso-types';
import { useState } from 'react';
import { useCanEditShippingClaim } from '~/features/shippingClaimReview/hooks/useCanEditShippingClaim';
import { useShippingClaimInventoryLevelsQuery } from '~/features/shippingClaimReview/hooks/useShippingClaimInventoryLevelsQuery';
import { useShippingClaimLineItemDelete } from '~/features/shippingClaimReview/hooks/useShippingClaimLineItemDelete';
import { useShippingClaim } from '~/features/shippingClaimReview/ShippingClaimReviewProvider';
import { useCurrency } from '~/hooks/useCurrency';
import { AddShippingClaimLineItem } from './AddShippingClaimLineItem';
import { LineItemInventoryDetail } from './LineItemInventoryDetail';

type ShippingClaimLineItem = ReturnType<
  typeof useShippingClaim
>['shippingClaimLineItems'][number];

const lineItemTagBadge = {
  [ShippingClaimLineItemTag.hasCustomization]: {
    label: 'Customized',
    variant: 'info',
    tooltip: 'This item was personalized when it was ordered.',
  },
  [ShippingClaimLineItemTag.removedFromOrder]: {
    label: 'Removed from order',
    variant: 'danger',
    tooltip: 'This item is no longer on the original order.',
  },
  [ShippingClaimLineItemTag.unfulfilled]: {
    label: 'Unfulfilled',
    variant: 'warning',
    tooltip: 'This item has not been fulfilled on the original order.',
  },
} as const satisfies Record<
  ShippingClaimLineItemTag,
  { label: string; variant: 'info' | 'danger' | 'warning'; tooltip: string }
>;

function LineItemRow({
  li,
  canEdit,
}: {
  li: ShippingClaimLineItem;
  canEdit: boolean;
}) {
  const { currencyCode } = useCurrency();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const { mutate: deleteLineItem, isPending } =
    useShippingClaimLineItemDelete();

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex-1">
        <OrderLineItem
          src={li.imgUrl}
          sku={li.sku}
          title={li.name}
          quantity={li.quantity}
          options={li.optionsFromPlatform}
          price={{ currencyCode, unitPrice: li.unitPrice }}
        >
          <OrderLineItem.Detail>
            <LineItemInventoryDetail lineItem={li} />
            {li.kind === 'replacement_item' && (
              <SimpleTooltip content="This item was added after the claim was created.">
                <Badge variant="info">Replacement item</Badge>
              </SimpleTooltip>
            )}
            {li.kind === 'line_item' &&
              li.tags.map((tag) => (
                <SimpleTooltip
                  key={tag}
                  content={lineItemTagBadge[tag].tooltip}
                >
                  <Badge variant={lineItemTagBadge[tag].variant}>
                    {lineItemTagBadge[tag].label}
                  </Badge>
                </SimpleTooltip>
              ))}
          </OrderLineItem.Detail>
        </OrderLineItem>
      </div>
      <div className="flex items-center">
        {canEdit && (
          <Action
            icon="remove"
            variant="ghost"
            accessibilityLabel="Remove line item"
            onClick={() => setShowConfirmDelete(true)}
          />
        )}
      </div>

      {canEdit && (
        <ConfirmModal
          title="Remove Line Item"
          prompt="Are you sure you want to remove this line item from the claim?"
          confirmText="Remove"
          cancelText="Cancel"
          variant="destructive"
          show={showConfirmDelete}
          loading={isPending}
          onConfirm={() =>
            deleteLineItem(li, {
              onSettled: () => setShowConfirmDelete(false),
            })
          }
          onCancel={() => setShowConfirmDelete(false)}
        />
      )}
    </div>
  );
}

export function ShippingClaimLineItemList() {
  const { id, shippingClaimLineItems, resolutionMethod } = useShippingClaim();
  const canEdit = useCanEditShippingClaim();
  const [showAdd, setShowAdd] = useState(false);
  const { data: inventoryLevels } = useShippingClaimInventoryLevelsQuery(id);

  // levels only load for Corso CX agents, so without them nothing is flagged for inventory
  const needsAttention = (li: ShippingClaimLineItem) =>
    (!!inventoryLevels &&
      !inventoryLevels.find(
        (level) => level.kind === li.kind && level.id === li.id,
      )?.inventoryExists) ||
    (li.kind === 'line_item' &&
      li.tags.some((tag) => lineItemTagBadge[tag].variant !== 'info'));

  const sortedLineItems = [...shippingClaimLineItems].sort(
    (a, b) => Number(needsAttention(b)) - Number(needsAttention(a)),
  );

  return (
    <Card>
      <Card.Line>
        <Card.Heading>{resolutionMethod} Line Items</Card.Heading>
        {canEdit && (
          <Action
            icon="add"
            variant="ghost"
            accessibilityLabel="Add line item"
            onClick={() => setShowAdd(true)}
          />
        )}
      </Card.Line>
      {sortedLineItems.map((li) => (
        <LineItemRow key={`${li.kind}-${li.id}`} li={li} canEdit={canEdit} />
      ))}

      {canEdit && (
        <AddShippingClaimLineItem
          show={showAdd}
          onClose={() => setShowAdd(false)}
        />
      )}
    </Card>
  );
}
